import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { QRCode } from 'antd';
import { PageHeader, Card, Button, Badge, LoadingState, Alert } from '../components/ui';
import accountService from '../services/accountService';
import accountLivenessService from '../services/accountLivenessService';
import massJoinService from '../services/massJoinService';
import MassGroupJoiningSection from '../components/MassGroupJoiningSection';
import AccountTelegramProfile from '../components/AccountTelegramProfile';
import AccountPrivacySettings from '../components/AccountPrivacySettings';
import AccountDeletionDialog from '../components/AccountDeletionDialog';
import {
  UserIcon,
  ArrowLeftIcon,
  TrashIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  PaperAirplaneIcon,
  KeyIcon,
  MagnifyingGlassIcon,
  LinkIcon,
  PlusIcon,
  PauseIcon,
  PlayIcon,
  StopIcon,
  UsersIcon
} from '@heroicons/react/24/outline';

const AccountDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeletionDialog, setShowDeletionDialog] = useState(false);
  const [deletionError, setDeletionError] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerificationForm, setShowVerificationForm] = useState(false);
  const [showQrAuthForm, setShowQrAuthForm] = useState(false);
  const [qrAuthLink, setQrAuthLink] = useState('');
  const [password, setPassword] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordHint, setPasswordHint] = useState('');
  const [username, setUsername] = useState('');
  const [foundUser, setFoundUser] = useState(null);
  const [message, setMessage] = useState('');
  const [chatLink, setChatLink] = useState('');

  const closeAuthForms = () => {
    setShowVerificationForm(false);
    setShowPasswordForm(false);
    setShowQrAuthForm(false);
  };

  const handleAuthFlowResponse = (data, fallbackMessage = null) => {
    if (!data) {
      return;
    }

    if (data.status === 'authorized') {
      closeAuthForms();
      setVerificationCode('');
      setPassword('');
      setQrAuthLink('');
      setSuccess(data.message || fallbackMessage || 'Account verified successfully');
      queryClient.invalidateQueries(['account', id]);
      queryClient.invalidateQueries('accounts');
      queryClient.invalidateQueries('sessions');
      return;
    }

    if (data.status === 'waiting_for_password') {
      setShowPasswordForm(true);
      setShowVerificationForm(false);
      setShowQrAuthForm(false);
      setPasswordHint(data.password_hint || '');
      setSuccess(data.message || fallbackMessage || 'Password required');
      return;
    }

    if (data.status === 'waiting_for_qr_confirmation') {
      setShowQrAuthForm(true);
      setShowVerificationForm(false);
      setShowPasswordForm(false);
      setQrAuthLink(data.qr_link || '');
      setSuccess(data.message || fallbackMessage || 'Scan QR code in Telegram');
      return;
    }

    if (data.status === 'waiting_for_code') {
      setShowVerificationForm(true);
      setShowPasswordForm(false);
      setShowQrAuthForm(false);
      setSuccess(data.message || fallbackMessage || 'Authorization started. Enter code');
      return;
    }

    if (fallbackMessage) {
      setSuccess(fallbackMessage);
    }
  };

  const isAuthFlowActive = showVerificationForm || showPasswordForm || showQrAuthForm;

  // Fetch account details
  const {
    data: account,
    isLoading,
    error: accountError
  } = useQuery(
    ['account', id],
    () => accountService.getAccount(id),
    {
      retry: 1,
      refetchInterval: 6000,
      keepPreviousData: true,
      onError: (err) => {
        console.error('Error fetching account:', err);
      }
    }
  );

  useQuery(
    ['account-auth-status', id],
    () => accountService.getAuthorizationStatus(id),
    {
      enabled: Boolean(id) && isAuthFlowActive,
      refetchInterval: 2500,
      refetchIntervalInBackground: true,
      onSuccess: (data) => {
        if (!data) {
          return;
        }

        if (data.status === 'authorized') {
          handleAuthFlowResponse(data);
          return;
        }

        if (data.status === 'waiting_for_password' || data.status === 'waiting_for_qr_confirmation') {
          handleAuthFlowResponse(data);
          return;
        }

        if (data.status === 'failed') {
          setError(data.message || 'Authorization failed. Please restart authorization.');
        }
      },
    }
  );

  // Update account status mutation
  const updateStatusMutation = useMutation(
    (status) => accountService.updateAccountStatus(id, status),
    {
      onSuccess: (data) => {
        if (data.warning) {
          setSuccess(`Account status updated, but with warning: ${data.warning}`);
        } else {
          setSuccess(`Account status updated successfully`);
        }
        queryClient.invalidateQueries(['account', id]);
      },
      onError: (err) => {
        setError(`Failed to update account status: ${err.message}`);
      }
    }
  );

  const checkConnectionMutation = useMutation(
    () => accountLivenessService.checkAccount(id),
    {
      onSuccess: () => {
        setSuccess('Connection status refreshed');
        queryClient.invalidateQueries(['account', id]);
        queryClient.invalidateQueries('accounts');
        queryClient.invalidateQueries('sessions');
      },
      onError: (err) => {
        setError(`Failed to refresh connection status: ${err.message}`);
      }
    }
  );

  // Delete account mutation
  const deleteAccountMutation = useMutation(
    (confirmation) => accountService.deleteAccount(id, confirmation),
    {
      onSuccess: (data) => {
        setShowDeletionDialog(false);
        setSuccess(data?.message || 'Аккаунт удалён');
        queryClient.invalidateQueries('accounts');
        queryClient.invalidateQueries('sessions');
        // Redirect to accounts list after a short delay
        setTimeout(() => {
          navigate('/accounts');
        }, 2000);
      },
      onError: (err) => {
        const message = err.response?.data?.message || 'Не удалось удалить аккаунт. Повторите попытку.';
        setDeletionError(message);
        setError(message);
        queryClient.invalidateQueries(['account', id]);
      }
    }
  );

  // Authorize account mutation
  const authorizeAccountMutation = useMutation(
    () => accountService.authorizeAccount(id),
    {
      onSuccess: (data) => {
        handleAuthFlowResponse(data, 'Authorization process started. Please enter the verification code.');
      },
      onError: (err) => {
        setError(`Failed to start authorization: ${err.message}`);
      }
    }
  );

  const authorizeAccountQrMutation = useMutation(
    () => accountService.authorizeAccountWithQr(id),
    {
      onSuccess: (data) => {
        handleAuthFlowResponse(data, 'QR authorization started. Scan the QR code in Telegram mobile app.');
      },
      onError: (err) => {
        setError(`Failed to start QR authorization: ${err.message}`);
      }
    }
  );

  const refreshQrAuthMutation = useMutation(
    () => accountService.refreshAccountQrAuth(id),
    {
      onSuccess: (data) => {
        handleAuthFlowResponse(data, 'QR code refreshed.');
      },
      onError: (err) => {
        setError(`Failed to refresh QR code: ${err.message}`);
      }
    }
  );

  // Verify account mutation
  const verifyAccountMutation = useMutation(
    (code) => accountService.verifyExistingAccount(id, code),
    {
      onSuccess: (data) => {
        handleAuthFlowResponse(data, 'Verification code accepted.');
      },
      onError: (err) => {
        setError(`Failed to verify account: ${err.message}`);
      }
    }
  );

  // Verify account with password mutation
  const verifyAccountWithPasswordMutation = useMutation(
    (password) => accountService.verifyExistingAccountWithPassword(id, password),
    {
      onSuccess: (data) => {
        handleAuthFlowResponse(data, 'Account verified successfully with 2FA password');
      },
      onError: (err) => {
        setError(`Failed to verify account with password: ${err.message}`);
      }
    }
  );

  // Search user mutation
  const searchUserMutation = useMutation(
    (username) => accountService.searchUser(id, username),
    {
      onSuccess: (data) => {
        setFoundUser(data.user);
        setSuccess(`User found: ${data.user.first_name} ${data.user.last_name || ''}`);
      },
      onError: (err) => {
        setError(`Failed to find user: ${err.message}`);
        setFoundUser(null);
      }
    }
  );

  // Send message mutation
  const sendMessageMutation = useMutation(
    ({ user_id, message }) => accountService.sendMessage(id, user_id, message),
    {
      onSuccess: () => {
        setSuccess('Message sent successfully');
        setMessage('');
      },
      onError: (err) => {
        setError(`Failed to send message: ${err.message}`);
      }
    }
  );
  
  // Join direct mutation
  const joinDirectMutation = useMutation(
    (link) => accountService.joinDirect(id, link),
    {
      onSuccess: (data) => {
        // Check if the response indicates a rate limit
        if (data.result && !data.result.success && data.result.error === 'RATE_LIMITED') {
          // Format the time remaining in a human-readable format
          const secondsRemaining = data.result.seconds_remaining;
          const minutes = Math.floor(secondsRemaining / 60);
          const seconds = secondsRemaining % 60;
          const timeString = minutes > 0 
            ? `${minutes} minute${minutes !== 1 ? 's' : ''} and ${seconds} second${seconds !== 1 ? 's' : ''}` 
            : `${seconds} second${seconds !== 1 ? 's' : ''}`;
          
          // Set error message with rate limit information
          setError(`Rate limit reached: ${data.result.message || `Please wait ${timeString} before trying again.`}`);
          
          // Clear the chat link input
          setChatLink('');
        } else if (data.result && data.result.success) {
          // Success case - joined the chat
          const chatInfo = [
            `Title: ${data.result.title}`,
            data.result.member_count ? `Members: ${data.result.member_count}` : '',
            data.result.posts_per_hour ? `Activity: ${data.result.posts_per_hour} posts/hour` : '',
            data.result.description ? `Description: ${data.result.description}` : ''
          ].filter(Boolean).join('\n');
          
          setSuccess(`Successfully joined chat: ${data.result.title}\n\n${chatInfo}`);
          setChatLink('');
        } else {
          // Generic success message as fallback
          setSuccess(`Successfully joined chat`);
          setChatLink('');
        }
      },
      onError: (err) => {
        // Check if the error message contains rate limit information
        if (err.response && err.response.data && err.response.data.result && 
            err.response.data.result.error === 'RATE_LIMITED') {
          const result = err.response.data.result;
          const secondsRemaining = result.seconds_remaining;
          const minutes = Math.floor(secondsRemaining / 60);
          const seconds = secondsRemaining % 60;
          const timeString = minutes > 0 
            ? `${minutes} minute${minutes !== 1 ? 's' : ''} and ${seconds} second${seconds !== 1 ? 's' : ''}` 
            : `${seconds} second${seconds !== 1 ? 's' : ''}`;
          
          setError(`Rate limit reached: Please wait ${timeString} before trying again.`);
        } else {
          setError(`Failed to join chat: ${err.message}`);
        }
      }
    }
  );

  // Handle status change
  const handleStatusChange = (status) => {
    if (window.confirm(`Are you sure you want to change the account status to ${status}?`)) {
      updateStatusMutation.mutate(status);
    }
  };

  // Handle delete account
  const handleDeleteAccount = () => {
    setDeletionError('');
    if (account.deletion_preserves_history || account.type === 'agent' || account.rate_limit_data?.messenger_agent_removal) {
      setShowDeletionDialog(true);
    } else if (window.confirm('Удалить аккаунт и его локальную сессию? Это действие нельзя отменить.')) {
      deleteAccountMutation.mutate();
    }
  };

  // Handle authorize account
  const handleAuthorizeAccount = () => {
    setShowQrAuthForm(false);
    setQrAuthLink('');
    authorizeAccountMutation.mutate();
  };

  const handleAuthorizeAccountQr = () => {
    setShowVerificationForm(false);
    setShowPasswordForm(false);
    setQrAuthLink('');
    authorizeAccountQrMutation.mutate();
  };

  const handleRefreshQrAuth = () => {
    refreshQrAuthMutation.mutate();
  };

  const handleVerifyAccount = (e) => {
    e.preventDefault();
    if (verificationCode) {
      verifyAccountMutation.mutate(verificationCode);
    }
  };

  const handleVerifyAccountWithPassword = (e) => {
    e.preventDefault();
    if (password) {
      verifyAccountWithPasswordMutation.mutate(password);
    }
  };

  const handleSearchUser = (e) => {
    e.preventDefault();
    if (username) {
      searchUserMutation.mutate(username);
    }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (foundUser && message) {
      sendMessageMutation.mutate({ user_id: foundUser.id, message });
    } else if (!foundUser) {
      setError('Please search for a user first');
    } else if (!message) {
      setError('Please enter a message');
    }
  };
  
  const handleDirectJoin = (e) => {
    e.preventDefault();
    if (chatLink) {
      joinDirectMutation.mutate(chatLink);
    }
  };

  // Loading state
  if (isLoading) {
    return <LoadingState message="Loading account details..." />;
  }

  // Error state
  if (accountError) {
    return (
      <div className="text-center">
        <Alert variant="error" className="mb-4">
          {accountError.message || 'Failed to load account details'}
        </Alert>
        <Button
          variant="secondary"
          icon={ArrowLeftIcon}
          onClick={() => navigate('/accounts')}
        >
          Back to Accounts
        </Button>
      </div>
    );
  }

  // Not found state
  if (!account) {
    return (
      <div className="text-center">
        <Alert variant="error" className="mb-4">
          Account not found
        </Alert>
        <Button
          variant="secondary"
          icon={ArrowLeftIcon}
          onClick={() => navigate('/accounts')}
        >
          Back to Accounts
        </Button>
      </div>
    );
  }

  const normalizedLiveStatus = account.status === 'off' ? 'offline' : (account.live_status || 'unknown');

  const formatDateTime = (value) => {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }
    return date.toLocaleString();
  };

  const formatRelativeTime = (value) => {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }
    const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
    if (seconds < 60) {
      return `${seconds}s ago`;
    }
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return `${minutes}m ago`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours}h ago`;
    }
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const getLiveStatusBadge = (liveStatus) => {
    switch (liveStatus) {
      case 'online':
        return <Badge variant="green">Online</Badge>;
      case 'offline':
        return <Badge variant="red">Offline</Badge>;
      case 'checking':
        return <Badge variant="yellow">Checking</Badge>;
      case 'error':
        return <Badge variant="red">Error</Badge>;
      case 'unknown':
      default:
        return <Badge variant="gray">Unknown</Badge>;
    }
  };

  const getConnectionFreshness = (checkedAt) => {
    if (!checkedAt) {
      return { label: 'Not checked yet', className: 'text-red-600' };
    }

    const parsed = new Date(checkedAt);
    if (Number.isNaN(parsed.getTime())) {
      return { label: 'Unknown check time', className: 'text-red-600' };
    }

    const ageSeconds = Math.floor((Date.now() - parsed.getTime()) / 1000);
    if (ageSeconds < 30) {
      return { label: 'Fresh now', className: 'text-green-600' };
    }

    if (ageSeconds < 180) {
      return { label: `${Math.floor(ageSeconds / 60)}m ago`, className: 'text-yellow-600' };
    }

    return { label: `${Math.floor(ageSeconds / 60)}m ago`, className: 'text-red-600' };
  };

  const getAuthorizationBadge = (state) => {
    switch (state) {
      case 'authorized':
        return <Badge variant="green">Authorized</Badge>;
      case 'waiting_for_code':
        return <Badge variant="yellow">Code Pending</Badge>;
      case 'waiting_for_password':
        return <Badge variant="yellow">2FA Pending</Badge>;
      case 'waiting_for_qr_confirmation':
        return <Badge variant="blue">QR Pending</Badge>;
      case 'code_verification_pending':
      case 'password_verification_pending':
      case 'authorizing':
        return <Badge variant="blue">In Progress</Badge>;
      case 'failed':
        return <Badge variant="red">Auth Failed</Badge>;
      case 'not_authorized':
        return <Badge variant="gray">Not Authorized</Badge>;
      default:
        return <Badge variant="gray">Unknown</Badge>;
    }
  };

  return (
    <div>
      <PageHeader
        title="Account Details"
        description={`Manage account ${account.phone || 'Unknown'}`}
        actions={
          <Button
            variant="secondary"
            icon={ArrowLeftIcon}
            onClick={() => navigate('/accounts')}
          >
            Back
          </Button>
        }
      />

      {error && (
        <Alert
          variant="error"
          className="mb-4"
          isDismissible
          onDismiss={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          variant="success"
          className="mb-4"
          isDismissible
          onDismiss={() => setSuccess('')}
        >
          {success}
        </Alert>
      )}

      {showDeletionDialog && <AccountDeletionDialog key={`delete-${id}`} accountId={id}
        busy={deleteAccountMutation.isLoading} error={deletionError}
        onCancel={() => { if (!deleteAccountMutation.isLoading) setShowDeletionDialog(false); }}
        onConfirm={confirmation => deleteAccountMutation.mutate(confirmation)} />}

      <AccountTelegramProfile
        key={`profile-${id}`}
        account={account}
        authInProgress={isAuthFlowActive}
        onSaved={() => {
          queryClient.invalidateQueries(['account', id]);
          queryClient.invalidateQueries('accounts');
        }}
      />

      <AccountPrivacySettings key={`privacy-${id}`} account={account} authInProgress={isAuthFlowActive} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title="Account Information">
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-500">Phone Number</h4>
              <p className="mt-1 text-sm text-gray-900">{account.phone || 'Not available'}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Name</h4>
              <p className="mt-1 text-sm text-gray-900">{account.name || 'Not available'}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Control (Manual)</h4>
              <div className="mt-1">
                {account.status === 'on' ? (
                  <Badge variant="green">Enabled</Badge>
                ) : account.status === 'off' ? (
                  <Badge variant="gray">Disabled</Badge>
                ) : account.status === 'banned' ? (
                  <Badge variant="red">Banned</Badge>
                ) : account.status === 'pending' ? (
                  <Badge variant="yellow">Pending</Badge>
                ) : (
                  <Badge variant="gray">{account.status || 'Unknown'}</Badge>
                )}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Connection (Runtime)</h4>
              {(() => {
                const freshness = getConnectionFreshness(account.connection_last_checked_at);
                return (
              <div className="mt-1 flex items-center gap-2">
                {getLiveStatusBadge(normalizedLiveStatus)}
                <Button
                  variant="secondary"
                  size="sm"
                  icon={ArrowPathIcon}
                  onClick={() => checkConnectionMutation.mutate()}
                  isLoading={checkConnectionMutation.isLoading}
                  disabled={checkConnectionMutation.isLoading || account.status !== 'on'}
                >
                  Check Now
                </Button>
                <span className={`text-xs ${freshness.className}`}>
                  {freshness.label}
                </span>
              </div>
                );
              })()}
              <p className="mt-1 text-xs text-gray-500">
                Last runtime check: {formatDateTime(account.connection_last_checked_at)}
              </p>
              {account.status !== 'on' && (
                <p className="mt-2 text-xs text-gray-500">
                  Connection checks are available when account control is enabled.
                </p>
              )}
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Authorization</h4>
              <div className="mt-1 space-y-2">
                {getAuthorizationBadge(account.authorization_state)}
                <div className="text-xs text-gray-600">
                  Last auth event: {formatDateTime(account.authorization_last_event_at)}
                </div>
                <div className="text-xs text-gray-600">
                  Last code request: {formatDateTime(account.authorization_last_code_requested_at)}
                </div>
                {account.authorization_last_error && (
                  <div className="text-xs text-red-600 break-words">
                    {account.authorization_last_error}
                  </div>
                )}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Activity Timeline</h4>
              <div className="mt-1 space-y-1 text-sm text-gray-900">
                <div>
                  Last activity: {formatRelativeTime(account.last_activity_at)} ({formatDateTime(account.last_activity_at)})
                </div>
                <div>
                  Last online: {formatDateTime(account.last_online_at)}
                </div>
                <div>
                  Offline since: {formatDateTime(account.offline_since)}
                </div>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Full</h4>
              <p className="mt-1 text-sm text-gray-900">
                {account.full ? 'Yes' : 'No'}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Groups</h4>
              <p className="mt-1 text-sm text-gray-900">
                {account.group_count || 0}
              </p>
            </div>
            {account.created_at && (
              <div>
                <h4 className="text-sm font-medium text-gray-500">Created</h4>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(account.created_at).toLocaleString()}
                </p>
              </div>
            )}
            {account.updated_at && (
              <div>
                <h4 className="text-sm font-medium text-gray-500">Last Updated</h4>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(account.updated_at).toLocaleString()}
                </p>
              </div>
            )}
          </div>
        </Card>

        <Card title="Account Management">
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Control Switch</h4>
              <p className="text-sm text-gray-500 mb-4">
                This is the manual on/off switch. Runtime connection is shown separately above.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={account.status === 'on' ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => handleStatusChange('on')}
                  disabled={account.status === 'on' || updateStatusMutation.isLoading}
                  icon={CheckCircleIcon}
                >
                  Enable
                </Button>
                <Button
                  variant={account.status === 'off' ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => handleStatusChange('off')}
                  disabled={account.status === 'off' || updateStatusMutation.isLoading}
                  icon={XCircleIcon}
                >
                  Disable
                </Button>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Session</h4>
              <p className="text-sm text-gray-500 mb-4">
                Manage the Telegram session associated with this account.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  onClick={() => navigate(`/sessions/${id}`)}
                >
                  View Session Details
                </Button>
                <Button
                  variant="warning"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to reset this session? This will delete all session files and the account will need to be re-authenticated.')) {
                      fetch(`/backend/sessions/${id}/reset`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'x-auth-token': localStorage.getItem('token')
                        }
                      })
                      .then(res => res.json())
                      .then(data => {
                        setSuccess(data.message || 'Session reset successfully');
                        queryClient.invalidateQueries(['account', id]);
                      })
                      .catch(err => {
                        setError(`Failed to reset session: ${err.message}`);
                      });
                    }
                  }}
                >
                  Reset Session
                </Button>
                <Button
                  variant="primary"
                  icon={KeyIcon}
                  onClick={handleAuthorizeAccount}
                  isLoading={authorizeAccountMutation.isLoading}
                  disabled={authorizeAccountMutation.isLoading || authorizeAccountQrMutation.isLoading}
                >
                  Authorize Account
                </Button>
                <Button
                  variant="secondary"
                  icon={LinkIcon}
                  onClick={handleAuthorizeAccountQr}
                  isLoading={authorizeAccountQrMutation.isLoading}
                  disabled={authorizeAccountMutation.isLoading || authorizeAccountQrMutation.isLoading}
                >
                  Authorize via QR
                </Button>
              </div>

              {showQrAuthForm && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h5 className="font-medium text-gray-700 mb-2">Scan QR in Telegram</h5>
                  <p className="text-sm text-gray-500 mb-4">
                    On your phone: Telegram - Settings - Devices - Link Desktop Device.
                  </p>
                  <div className="flex justify-center py-2">
                    {qrAuthLink ? (
                      <QRCode value={qrAuthLink} size={220} />
                    ) : (
                      <div className="text-sm text-gray-500">QR code is being generated...</div>
                    )}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button
                      variant="secondary"
                      icon={ArrowPathIcon}
                      onClick={handleRefreshQrAuth}
                      isLoading={refreshQrAuthMutation.isLoading}
                      disabled={refreshQrAuthMutation.isLoading || !showQrAuthForm}
                    >
                      Refresh QR
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setShowQrAuthForm(false);
                        setQrAuthLink('');
                      }}
                    >
                      Hide QR
                    </Button>
                  </div>
                </div>
              )}

              {showVerificationForm && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h5 className="font-medium text-gray-700 mb-2">Enter Verification Code</h5>
                  <form onSubmit={handleVerifyAccount} className="space-y-4">
                    <div>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Verification code"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        required
                      />
                    </div>
                    <Button
                      type="submit"
                      variant="primary"
                      isLoading={verifyAccountMutation.isLoading}
                      disabled={verifyAccountMutation.isLoading}
                    >
                      Verify
                    </Button>
                  </form>
                </div>
              )}

              {showPasswordForm && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h5 className="font-medium text-gray-700 mb-2">Enter 2FA Password</h5>
                  {passwordHint && (
                    <p className="text-sm text-gray-500 mb-2">Hint: {passwordHint}</p>
                  )}
                  <form onSubmit={handleVerifyAccountWithPassword} className="space-y-4">
                    <div>
                      <input
                        type="password"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        placeholder="2FA Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </div>
                    <Button
                      type="submit"
                      variant="primary"
                      isLoading={verifyAccountWithPasswordMutation.isLoading}
                      disabled={verifyAccountWithPasswordMutation.isLoading}
                    >
                      Submit Password
                    </Button>
                  </form>
                </div>
              )}
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Test Messaging</h4>
              <p className="text-sm text-gray-500 mb-4">
                Search for a user by username and send a test message.
              </p>

              <form onSubmit={handleSearchUser} className="mb-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Username (without @)"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                  <Button
                    type="submit"
                    variant="secondary"
                    icon={MagnifyingGlassIcon}
                    isLoading={searchUserMutation.isLoading}
                    disabled={searchUserMutation.isLoading}
                  >
                    Search
                  </Button>
                </div>
              </form>

              {foundUser && (
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <h5 className="font-medium text-gray-700 mb-2">User Found</h5>
                  <div className="space-y-2">
                    <p><span className="font-medium">ID:</span> {foundUser.id}</p>
                    <p><span className="font-medium">Username:</span> {foundUser.username ? `@${foundUser.username}` : 'N/A'}</p>
                    <p><span className="font-medium">Name:</span> {foundUser.first_name} {foundUser.last_name || ''}</p>
                  </div>

                  {account.type === 'agent' ? (
                    <form onSubmit={handleSendMessage} className="mt-4 space-y-4">
                      <div>
                        <textarea
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Message to send"
                          rows="3"
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          required
                        ></textarea>
                      </div>
                      <Button
                        type="submit"
                        variant="primary"
                        icon={PaperAirplaneIcon}
                        isLoading={sendMessageMutation.isLoading}
                        disabled={sendMessageMutation.isLoading}
                      >
                        Send Message
                      </Button>
                    </form>
                  ) : (
                    <p className="mt-4 text-sm text-gray-500">
                      Direct sending is available only for accounts with the Agent Messenger role.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Direct Join Chat/Topic</h4>
              <p className="text-sm text-gray-500 mb-4">
                Directly join a chat or topic by providing a link (e.g., https://t.me/username or https://t.me/username/topic_id).
              </p>

              <form onSubmit={handleDirectJoin} className="mb-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="https://t.me/username or https://t.me/username/topic_id"
                    value={chatLink}
                    onChange={(e) => setChatLink(e.target.value)}
                    required
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    icon={LinkIcon}
                    isLoading={joinDirectMutation.isLoading}
                    disabled={joinDirectMutation.isLoading}
                  >
                    Join
                  </Button>
                </div>
              </form>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Удаление аккаунта</h4>
              <p className="text-sm text-gray-500 mb-4">
                Аккаунты с историей Messenger удаляются из рабочего списка с сохранением переписки.
              </p>
              <Button
                variant="danger"
                icon={TrashIcon}
                onClick={handleDeleteAccount}
                isLoading={deleteAccountMutation.isLoading}
                disabled={deleteAccountMutation.isLoading}
              >
                Удалить аккаунт
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Mass Group Joining Section */}
      <Card title="Mass Group Joining" className="mt-6">
        <div className="prose max-w-none mb-4">
          <p>
            Add multiple groups to this account at once. This process respects Telegram's rate limits to avoid restrictions.
          </p>
        </div>

        {/* Mass Join Form */}
        <MassGroupJoiningSection accountId={id} setError={setError} setSuccess={setSuccess} />
      </Card>

      <Card title="Groups" className="mt-6">
        <div className="prose max-w-none mb-4">
          <p>
            Groups that this account has joined. Showing the first {account.groups?.length || 0}
            {account.groups_truncated ? ` of ${account.group_count || 0}` : ''}. You can view all groups on the Groups page.
          </p>
        </div>

        {account.groups && account.groups.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Group
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Members
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quality
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {account.groups.map((group) => (
                  <tr key={group.chat_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {group.title}
                      </div>
                      {group.chat_link && (
                        <div className="text-sm text-gray-500">
                          <a
                            href={group.chat_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-900"
                          >
                            {group.chat_username ? `@${group.chat_username}` : group.chat_link}
                          </a>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {group.member_count || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {group.quality >= 70 ? (
                        <Badge variant="green">High ({group.quality})</Badge>
                      ) : group.quality >= 40 ? (
                        <Badge variant="yellow">Medium ({group.quality})</Badge>
                      ) : (
                        <Badge variant="red">Low ({group.quality})</Badge>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Button
                        variant="secondary"
                        size="sm"
                        to={`/groups/${group.chat_id}`}
                      >
                        Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center text-gray-500 py-4">
            This account has not joined any groups yet.
          </p>
        )}
      </Card>
    </div>
  );
};

export default AccountDetail;
